/**
 * Handles responsive navigation menu toggling and accessibility features
 * including focus trapping and keyboard navigation.
 *
 * @param {HTMLElement} toggleButton - The menu toggle button element
 * @returns {void}
 */
const responsiveToggle = (toggleButton) => {
  if (!toggleButton) return;

  // Cache DOM elements
  const navigationMenu = document.querySelector('.header__nav');
  const ctaMenu = document.querySelector('.header__ctas');
  const headerContainer = document.querySelector('header[role="banner"] .container');

  /**
   * Retrieves the navigation breakpoint from CSS custom property
   * @returns {number} Breakpoint value in pixels
   */
  const getNavBreakpoint = () => {
    const breakpoint = getComputedStyle(document.documentElement)
      .getPropertyValue('--nav-breakpoint')
      .trim();
    return parseInt(breakpoint);
  };

  /**
   * Checks if viewport width is above the navigation breakpoint
   * @returns {boolean}
   */
  const isAboveBreakpoint = () => window.innerWidth >= getNavBreakpoint();

  /**
   * Updates toggle button state and appearance
   * @param {boolean} isOpen - Whether the menu is open
   */
  const updateToggleState = (isOpen) => {
    const attrs = {
      'aria-expanded': isOpen,
      'aria-label': `${isOpen ? 'Close' : 'Open'} menu navigation`
    };

    const icon = isOpen
      ? `<svg width="20" height="20" aria-hidden="true" fill="var(--primary-100)" xmlns="http://www.w3.org/2000/svg">
           <path d="M10 10 2 2m8 8 8 8m-8-8 8-8m-8 8-8 8" stroke="var(--primary-100)" stroke-width="2.667" stroke-linecap="round" stroke-linejoin="round"/>
         </svg>`
      : `<svg width="26" height="21" aria-hidden="true" fill="var(--primary-100)" xmlns="http://www.w3.org/2000/svg">
           <path d="M2 1.667h24m-24 8h24m-24 8h24" stroke="var(--primary-100)" stroke-width="2.667" stroke-linecap="round" stroke-linejoin="round"/>
         </svg>`;

    Object.entries(attrs).forEach(([key, value]) => toggleButton.setAttribute(key, value));
    toggleButton.innerHTML = icon;
  };

  /**
   * Implements focus trap within the menu
   * @param {HTMLElement} container - The container to trap focus within
   * @param {HTMLElement} closeButton - Button to focus on when closing
   */
  const focusTrap = (container, closeButton) => {
    const focusableSelector = [
      'button',
      'a:not(.skiplink)',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    const focusableElements = container.querySelectorAll(focusableSelector);
    const firstFocusable = focusableElements[1];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable?.focus();

    const handleKeydown = (event) => {
      const { key, shiftKey } = event;

      if (key === 'Escape') {
        closeMenu();
        return;
      }

      if (key !== 'Tab') return;

      if (shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable?.focus();
        return;
      }

      if (!shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable?.focus();
      }
    };

    const cleanup = () => {
      container.removeEventListener('keydown', handleKeydown);
      closeButton.removeEventListener('click', cleanup);
      closeButton.focus();
    };

    container.addEventListener('keydown', handleKeydown);
    closeButton.addEventListener('click', cleanup);
  };

  /**
   * Closes the mobile menu and resets states
   */
  const closeMenu = () => {
    navigationMenu?.classList.remove('show');
    ctaMenu?.classList.remove('show');
    updateToggleState(false);
  };

  /**
   * Resets mobile menu when viewport is above breakpoint
   */
  const resetMobileMenu = () => {
    if (isAboveBreakpoint()) {
      closeMenu();
    }
  };

  // Event Listeners
  let prevWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (Math.abs(window.innerWidth - prevWidth) < 10) return;
    prevWidth = window.innerWidth;
    resetMobileMenu();
  });

  toggleButton.addEventListener('click', () => {
    navigationMenu?.classList.toggle('show');
    ctaMenu?.classList.toggle('show');

    const isOpen = navigationMenu?.classList.contains('show');
    updateToggleState(isOpen);

    if (isOpen) {
      focusTrap(headerContainer, toggleButton);
    }
  });
};

export default responsiveToggle;
